import React, { useCallback } from 'react'
import { ProductImage } from 'app/common/Common'
import _ from 'lodash'
import {
  DcButton,
  DcDropDown,
  DcFlexBox,
  DcInput,
  PixelFactoryContext,
  PixelTable,
  PixelText,
  Section,
} from 'pixel-base'
import styled from 'styled-components'

import { get } from 'utils/api-routes.util'
import { IPixelFactory } from '../../../../../../../../decl'

interface IProduct {
  product_id: number
  product_name: string
  product_sku?: string
  sku?: string
  product_image_path?: string
  image?: string
  price?: number
  stock_quantity?: number
}

interface SearchProductProps {
  searchValue: string
  handleClose: (selectedItems?: any) => void
  categories: any[]
  cart: IProduct[]
}

// Styled components for store display
const StoreChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`

const StoreChip = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
  white-space: nowrap;
`

const LinkMissingError = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  white-space: nowrap;
`

const StatusBadge = styled.span<{ $isActive: boolean }>`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${(props) => (props.$isActive ? '#d4edda' : '#f8d7da')};
  color: ${(props) => (props.$isActive ? '#155724' : '#721c24')};
  border: 1px solid ${(props) => (props.$isActive ? '#c3e6cb' : '#f5c6cb')};
  white-space: nowrap;
`

const SearchProduct = (props: SearchProductProps) => {
  const { searchValue, handleClose, categories, cart } = props
  const [tableLoading, setTableLoader] = React.useState(true)
  const { showFailure } = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [Catalogue, setCatalogue] = React.useState<IProduct[]>([])
  const [selectedItems, setSelectedItems] = React.useState<IProduct[]>([])
  const [value, setValue] = React.useState(searchValue)
  const [catId, setcatId] = React.useState(null)

  const columns = [
    {
      dataKey: 'image',
      name: 'Image',
      width: '25%',
      selector: (row) => (
        <DcFlexBox alignItems="center" gap="5px" height="50px" width="auto">
          <ProductImage
            style={{
              width: '28px',
              marginRight: '5px',
            }}
            path={row.image}
          />
          <PixelText multiLine style={{ fontSize: '12px' }}>
            {row.product_name}
          </PixelText>
        </DcFlexBox>
      ),
    },
    {
      dataKey: 'sku',
      name: 'SKU',
      width: '18%',
      sortable: true,
      selector: (row) => (
        <PixelText style={{ fontSize: '12px' }}>{row.sku || '--'}</PixelText>
      ),
    },
    {
      dataKey: 'product_status',
      name: 'Status',
      width: '10%',
      sortable: true,
      selector: (row) => {
        const isInCart = cart.some(
          (cartItem) => cartItem.product_id === row.product_id,
        )
        return (
          <DcFlexBox flexDirection="column" gap="2px">
            <StatusBadge $isActive={row.product_status === 'active'}>
              {row.product_status === 'active' ? 'Active' : 'Inactive'}
            </StatusBadge>
            {isInCart && (
              <StatusBadge
                $isActive={false}
                style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  border: '1px solid #ffeaa7',
                }}
              >
                Added
              </StatusBadge>
            )}
          </DcFlexBox>
        )
      },
    },
    {
      dataKey: 'stock_quantity',
      name: 'Stock',
      width: '10%',
      sortable: true,
      selector: (row) => (
        <PixelText style={{ fontSize: '12px' }}>
          {row.stock_quantity || 0}
        </PixelText>
      ),
    },
    {
      dataKey: 'price',
      name: 'Price',
      width: '12%',
      sortable: true,
      selector: (row) => (
        <PixelText style={{ fontSize: '12px' }}>{row.price || '--'}</PixelText>
      ),
    },
    {
      dataKey: 'store_links',
      name: 'Stores',
      width: '15%',
      sortable: false,
      selector: (row) => (
        <StoreChipsContainer>
          {row.store_links && row.store_links.length > 0 ? (
            row.store_links.map((store, index) => (
              <StoreChip key={`${store.store_id || index}-${index}`}>
                {store.store_name}
              </StoreChip>
            ))
          ) : (
            <LinkMissingError>Link Missing</LinkMissingError>
          )}
        </StoreChipsContainer>
      ),
    },
  ]
  React.useEffect(() => {
    setValue(searchValue)
  }, [searchValue])

  const getAllCatalogue = (query) => {
    setTableLoader(true)
    get(`/products/lineitems?search=${query}`).subscribe((res: any) => {
      if (!res.response.error) {
        setCatalogue(res.response)
        setTableLoader(false)
      } else {
        showFailure(res.response.error)
      }
    })
  }
  React.useEffect(() => {
    getAllCatalogue(value)
  }, [catId, value])

  // Clear selected items that are already in the cart
  React.useEffect(() => {
    if (selectedItems.length > 0) {
      const filteredSelectedItems = selectedItems.filter(
        (selectedItem) =>
          !cart.some(
            (cartItem) => cartItem.product_id === selectedItem.product_id,
          ),
      )
      if (filteredSelectedItems.length !== selectedItems.length) {
        setSelectedItems(filteredSelectedItems)
      }
    }
  }, [cart, selectedItems])

  const handleRowChange = (row) => {
    if (row) {
      // Filter out products that are already in the cart
      const filteredSelectedRows = row.selectedRows.filter(
        (selectedRow) =>
          !cart.some(
            (cartItem) => cartItem.product_id === selectedRow.product_id,
          ),
      )
      setSelectedItems(filteredSelectedRows)
    }
  }
  const handleDebounceFn = (value) => {
    getAllCatalogue(value)
  }

  const debounceFn = useCallback(_.debounce(handleDebounceFn, 500), [])

  // Helper function to filter out products already in cart
  const getFilteredSelectedItems = useCallback(() => {
    return selectedItems.filter(
      (selectedItem) =>
        !cart.some(
          (cartItem) => cartItem.product_id === selectedItem.product_id,
        ),
    )
  }, [selectedItems, cart])
  return (
    <>
      <Section>
        <Header>
          <DcInput
            showsearchicon={0}
            inputSize="small"
            style={{ backgroundColor: '#f6f6f6' }}
            maxLength={20}
            placeholder="Search"
            name="search"
            onChange={(ev) => {
              debounceFn(ev.target.value)
              setValue(ev.target.value)
            }}
            value={value}
          />
          <DcDropDown
            dropdownSize="sm"
            required
            value={catId || ''}
            onChange={(e) => setcatId(e.target.value)}
            placeholder="Select Collections"
            name="Select Collections"
            customLabel=""
            options={[
              { label: 'Select Collections', value: '' },
              ...(categories || []),
            ].map((key) => {
              return {
                label: key.label,
                value: key.value,
              }
            })}
          />
        </Header>
        <Content>
          <PixelTable
            columns={columns}
            data={Catalogue || []}
            highlightOnHover={true}
            progressPending={tableLoading}
            selectableRows
            fixedHeader={true}
            fixedHeaderScrollHeight="calc(100vh - 235px)"
            {...{
              selectableRowDisabled: (row) => {
                // Check if this product is already in the cart
                return cart.some(
                  (cartItem) => cartItem.product_id === row.product_id,
                )
              },
            }}
            onSelectedRowsChange={handleRowChange}
            selectableRowsHighlight={true}
          />
        </Content>
        <FormGroup flexEnd>
          <DcButton
            margin="5px"
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              handleClose()
            }}
          >
            Close
          </DcButton>
          <DcButton
            margin="5px"
            onClick={() => {
              handleClose(getFilteredSelectedItems())
            }}
            size="sm"
            variant="primary"
            type="submit"
            disabled={getFilteredSelectedItems().length === 0}
          >
            Add {getFilteredSelectedItems().length} product
            {getFilteredSelectedItems().length !== 1 ? 's' : ''} to list
          </DcButton>
        </FormGroup>
      </Section>
    </>
  )
}

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  width: 100%;
  margin-bottom: 10px;
`
const Content = styled.div`
  width: 100%;
  border: 1px solid #ccc;
  height: auto;
  max-height: calc(100vh - 200px);
  overflow: auto;
`
const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  width: 100%;
  margin-top: 10px;
`
export default SearchProduct
